import type { Dispatch, SetStateAction } from 'react';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useTurnstile } from 'react-turnstile';

import { notifications } from '@mantine/notifications';

import { IconMoodPuzzled } from '@tabler/icons-react';
import { useAppDispatch } from '@xver/hooks/useAppDispatch';
import { setPhoneVerificationData } from '@xver/store/authPage/authPage.actions';
import { Endpoints } from '@xver/apis';
import type { PageType } from '../../../constants';
import { PAGE_TYPE } from '../../../constants';

interface SignUpValues {
    email: string;
    password: string;
    confirmPassword?: string;
    phoneNumber: string;
    turnstileToken: string;
}

interface useSignUpProps {
    setPageType: Dispatch<SetStateAction<PageType>>;
}

export function useSignUp({ setPageType }: useSignUpProps) {
    const dispatch = useAppDispatch();
    const turnstile = useTurnstile();
    const { t } = useTranslation('notifications');

    const [signUpValues, setSignUpValues] = useState<SignUpValues>({
        email: '',
        password: '',
        confirmPassword: '',
        phoneNumber: '',
        turnstileToken: '',
    });

    const {
        formState: { isSubmitting, errors },
        handleSubmit,
        register,
        setValue,
        watch,
    } = useForm({
        mode: 'onChange',
        defaultValues: {
            email: '',
            password: '',
            confirmPassword: '',
            phoneNumber: '',
            turnstileToken: '',
        },
    });

    const onSubmit = handleSubmit(async (values) => {
        try {
            const cleanedPhoneNumber = (values.phoneNumber || '').replace(/-/g, '');

            setSignUpValues({
                email: values.email,
                password: values.password,
                phoneNumber: cleanedPhoneNumber,
                turnstileToken: values.turnstileToken,
            });

            const response = await Endpoints.v1.Authentication.signUp({
                email: values.email,
                password: values.password,
                confirmPassword: values.confirmPassword,
                phoneNumber: cleanedPhoneNumber,
                turnstileToken: values.turnstileToken,
            });
            if (response.data) {
                const { phoneNumber, verificationToken } = response.data;

                dispatch(setPhoneVerificationData({ phoneNumber, verificationToken }));
                setPageType(PAGE_TYPE.PHONE_NUMBER_VERIFICATION);
            }
        } catch ({ message }: any) {
            notifications.show({
                title: t('auth.signUpFailed'),
                message: message || t('auth.signUpFailedMessage'),
                icon: <IconMoodPuzzled size={32} color={'#eb5757'} />,
            });

            turnstile.reset();
            setValue('turnstileToken', '');
        }
    });

    return { signUpValues, isSubmitting, errors, register, setValue, watch, onSubmit };
}
